import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import LuggageIcon from "@mui/icons-material/Luggage";
import FiberNewIcon from "@mui/icons-material/FiberNew";

function buildLabel(p) {
  const suffix =
    p.personType === "ADULT"
      ? " (Adult)"
      : "";
  return `${p.personName || "Unknown"}${suffix}`;
}

function isUnassignedClass(className) {
  return String(className || "").trim().toLowerCase() === "unassigned";
}

function getClassCardStyles(className, colorIndex) {
  if (isUnassignedClass(className)) {
    return {
      backgroundColor: "#fdeaea",
      border: "1px solid #e6b3b3",
    };
  }

  const palette = [
    { backgroundColor: "#eaf4ea", border: "1px solid #b9d7b9" },
    { backgroundColor: "#eaf2fd", border: "1px solid #b8cdee" },
    { backgroundColor: "#fff8e6", border: "1px solid #ead79c" },
    { backgroundColor: "#f3ecfb", border: "1px solid #ccb7e8" },
    { backgroundColor: "#eaf7f6", border: "1px solid #b7dcd8" },
  ];

  return palette[colorIndex % palette.length];
}

export default function ClassCard({
  className,
  residents = [],
  colorIndex = 0,
  tv = false,
  tvColumns = 3,
  teacherFallback = "",
}) {
  const [openState, setOpen] = useState(false);
  const open = tv || openState;
  const isUnassigned = isUnassignedClass(className);

  const firstWithMeta = residents.find(
    (p) => p.teacherName || p.classLocation
  ) || {};

  // Empty classes have no people carrying teacher meta — fall back to the
  // teacher from the class list so the card still shows who's teaching.
  const teacherName = firstWithMeta.teacherName || teacherFallback || "";
  const classLocation = firstWithMeta.classLocation || "";

  return (
    <Card
      sx={{
        width: tv ? tvColumns * 250 : 320,
        ...getClassCardStyles(className, colorIndex),
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isUnassigned ? "error.main" : "inherit",
              }}
            >
              {className}
            </Typography>

            {teacherName ? (
              <Typography variant="body2" color="text.secondary">
                Teacher: {teacherName}
              </Typography>
            ) : null}

            {classLocation ? (
              <Typography variant="body2" color="text.secondary">
                Location: {classLocation}
              </Typography>
            ) : null}
          </Stack>

          {tv ? null : (
            <IconButton onClick={() => setOpen((prev) => !prev)}>
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Stack>

        <Typography variant="body2" sx={{ mt: 1 }}>
          {residents.length} members
        </Typography>

        <Collapse in={open}>
          <Box sx={{ mt: 2, columnCount: tv ? tvColumns : 1, columnGap: "24px" }}>
            {residents.map((p) => {
              const arrivingToday = p.arrivingToday === 1;
              const departingToday = p.departingToday === 1;

              return (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  key={`${p.personType}-${p.personId}`}
                  sx={{ breakInside: "avoid", mb: 1 }}
                >
                  {arrivingToday && (
                    <FiberNewIcon color="primary" fontSize="small" />
                  )}

                  {departingToday && (
                    <LuggageIcon color="action" fontSize="small" />
                  )}

                  {isUnassigned && (
                    <WarningIcon color="warning" fontSize="small" />
                  )}

                  <Chip
                    label={buildLabel(p)}
                    color={departingToday ? "warning" : "default"}
                    size="small"
                  />
                </Stack>
              );
            })}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}